import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { FloatingTooltip } from "../../../common/components/Tooltip/FloatingTooltip";
import { useTourView } from "../../onboarding/engine/useTourView";
import { hostedTourAnchors } from "../../onboarding/engine/tourViewAnchors";

export type StatsTab =
  | "global"
  | "parcours"
  | "apprenants"
  | "classement"
  | "comparateur"
  | "reports";

interface StatsTabsProps {
  activeTab?: StatsTab;
  onChange?: (tab: StatsTab) => void;
  /** Tabs rendered grayed-out and non-navigable (e.g. when the org has no session at all). */
  disabledTabs?: StatsTab[];
  /** Tooltip shown on disabled tabs explaining why they are unavailable. */
  disabledTooltip?: string;
}

const StatsTabs = ({ activeTab, onChange, disabledTabs = [], disabledTooltip }: StatsTabsProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const tabs: { key: StatsTab; label: string; path: string }[] = [
    {
      key: "global",
      label: t("statsNew.tabs.global"),
      path: "/statistics",
    },
    {
      key: "parcours",
      label: t("statsNew.tabs.parcours"),
      path: "/statistics/parcours",
    },
    {
      key: "apprenants",
      label: t("statsNew.tabs.apprenants"),
      path: "/statistics/apprenants",
    },
    {
      key: "classement",
      label: t("statsNew.tabs.classement"),
      path: "/statistics/classement",
    },
    {
      key: "comparateur",
      label: t("statsNew.tabs.comparateur"),
      path: "/statistics/comparateur",
    },
    {
      key: "reports",
      label: t("statsNew.tabs.reports"),
      path: "/statistics/reports",
    },
  ];

  useTourView(
    "stats-tabs",
    true,
    hostedTourAnchors(
      "stats-tabs",
      tabs.filter((tab) => !disabledTabs.includes(tab.key)).map((tab) => `stats-tab-${tab.key}`),
    ),
  );

  const getIsActive = (path: string, key: StatsTab): boolean => {
    const pathname = location.pathname;

    if (key === "global") {
      return pathname === "/statistics" || pathname === "/statistics/";
    }

    return pathname.startsWith(path);
  };

  return (
    <div className="flex border-b border-gray-200">
      {tabs.map((tab) => {
        const isActive = activeTab ? activeTab === tab.key : getIsActive(tab.path, tab.key);

        if (disabledTabs.includes(tab.key)) {
          return (
            <FloatingTooltip key={tab.key} content={disabledTooltip ?? ""} wrap>
              <span
                aria-disabled="true"
                className="px-6 py-3 text-sm font-medium text-gray-300 cursor-not-allowed select-none"
              >
                {tab.label}
              </span>
            </FloatingTooltip>
          );
        }

        if (onChange) {
          return (
            <button
              key={tab.key}
              onClick={() => onChange(tab.key)}
              data-tour={`stats-tab-${tab.key}`}
              className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                isActive ? "text-green-600" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
              {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />}
            </button>
          );
        }

        return (
          <NavLink
            key={tab.key}
            to={tab.path}
            data-tour={`stats-tab-${tab.key}`}
            className={`px-6 py-3 text-sm font-medium transition-colors relative ${
              isActive ? "text-green-600" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />}
          </NavLink>
        );
      })}
    </div>
  );
};

export default StatsTabs;
